// Typed wrappers over the broker submission BFF (PMS-S125, now live). A
// submission is per-product/coverage line, own-scoped to the session brokerage.
// Mirrors the shape of the other broker seams (src/lib/brokerAccounts.ts): thin
// functions over the authed requester, types derived from the PMS contract
// (docs/openapi.yaml → brokerSubmissionDraftSchema + the enriched list item).
import { ApiError } from "@/lib/api";
import type { AuthedRequester } from "@/lib/brokerAuth";
import type { Page } from "@/lib/brokerAccounts";
import type { FieldType, MissingField } from "@/lib/brokerProducts";

export type { MissingField };

const BASE = "/api/broker/submissions";

/** Submission lifecycle status (mirrors the PMS submission_status enum). */
export type SubmissionStatus =
  | "draft"
  | "awaiting_triage"
  | "in_review"
  | "requested_info"
  | "awaiting_approval"
  | "quoted"
  | "bound"
  | "declined"
  | "deferred";

/**
 * A created submission. PMS returns the full submission detail; the intake flow
 * only needs the confirmation fields (the SUB number + status).
 */
export interface Submission {
  id: string;
  submissionNumber: string;
  accountId: string;
  status: SubmissionStatus;
  createdAt: string;
}

/**
 * A prior submission on an account, enriched for the renewal picker
 * (PMS-S125 brokerSubmissionListItem): human coverage label + line key + display
 * limit + effective/expiration + status, and the resulting carrier once the
 * submission is quoted/bound (null until then). PMS submissions are single-layer
 * — there is no primary/excess "layer".
 */
export interface AccountSubmission {
  id: string;
  submissionNumber: string;
  accountId: string;
  accountLegalName: string | null;
  coverage: string;
  coverageKey: string;
  projectedLimit: string | null;
  effectiveDate: string | null;
  expirationDate: string | null;
  status: SubmissionStatus;
  carrier: string | null;
  createdAt: string;
}

/**
 * The draft-create body (PMS-S125, product-addressed in PMS-S147). A submission
 * starts as a minimal draft on an EXISTING own account. The portal always sends
 * `productId` when a product was picked (a coverage line can carry several
 * products, so a bare `line` — still accepted by PMS — can be ambiguous); a
 * renewal names only the prior submission and PMS copies its product AND
 * carries its field bag forward (PMS-S148). `brokerageId` is never sent — PMS
 * forces it from the session.
 */
export interface CreateSubmissionInput {
  accountId: string;
  productId?: string;
  line?: string;
  renewalOfSubmissionId?: string;
}

/** POST /api/broker/submissions — start one draft submission (own-scoped). */
export function createSubmission(
  request: AuthedRequester,
  input: CreateSubmissionInput,
): Promise<Submission> {
  return request<Submission>(BASE, { method: "POST", body: input });
}

/** GET /api/broker/submissions?accountId=… — prior submissions for the renewal picker. */
export function listAccountSubmissions(
  request: AuthedRequester,
  accountId: string,
): Promise<{ items: AccountSubmission[] }> {
  return request<{ items: AccountSubmission[] }>(
    `${BASE}?accountId=${encodeURIComponent(accountId)}`,
  );
}

/**
 * The cheap existence check for a duplicate DRAFT (BRK-S063) — before starting a
 * new submission on the same account + product, ask whether one is already in
 * flight. Uses the PMS-S151 own-scoped `productId` + `status` filters and caps
 * at one row (we only need existence + a link target). Returns the first
 * matching draft, or null. A failed check is the caller's to swallow — it must
 * never block a legitimate create (NN #7).
 */
export async function findExistingDraft(
  request: AuthedRequester,
  accountId: string,
  productId: string,
): Promise<AccountSubmission | null> {
  const qs = new URLSearchParams({
    accountId,
    productId,
    status: "draft",
    limit: "1",
  });
  const page = await request<Page<AccountSubmission>>(
    `${BASE}?${qs.toString()}`,
  );
  return page.items[0] ?? null;
}

export interface SubmissionListQuery {
  search?: string;
  limit?: number;
  offset?: number;
}

/** GET /api/broker/submissions — the brokerage's own submissions (enriched, paged). */
export function listSubmissions(
  request: AuthedRequester,
  query: SubmissionListQuery = {},
): Promise<Page<AccountSubmission>> {
  const qs = new URLSearchParams();
  if (query.search) qs.set("search", query.search);
  if (query.limit != null) qs.set("limit", String(query.limit));
  if (query.offset != null) qs.set("offset", String(query.offset));
  const s = qs.toString();
  return request<Page<AccountSubmission>>(`${BASE}${s ? `?${s}` : ""}`);
}

/**
 * The submission detail (PMS-S125 GET /{id}) — the subset the detail view
 * shows. The full PMS record carries much more; the broker view needs the
 * header (number/status/dates), the account + product refs, and — since
 * PMS-S142 — the manifest FIELD BAG (`data`), which prefills the details step.
 * Bag keys/value shapes are declared by the product manifest (BRK-S051).
 * `carriedForwardKeys` (PMS-S148) names the bag keys a RENEWAL copied from the
 * prior submission (term dates never carry) — the details step marks them for
 * review-not-re-entry; empty on a fresh submission.
 */
export interface SubmissionDetail {
  id: string;
  submissionNumber: string;
  status: SubmissionStatus;
  effectiveDate: string | null;
  expirationDate: string | null;
  account: { id: string; accountNumber: string; legalName: string } | null;
  product: { id: string; name: string } | null;
  data: Record<string, unknown>;
  /** PMS-S150: canonical Code id → {id, code, description} for every classCode-
   *  typed value anywhere in `data` (a top-level classCode field or a classCode
   *  item inside a group, e.g. WC `exposures[].codeId`). Lets the details step
   *  render carried-forward class codes as code + description instead of the raw
   *  uuid (the renewal UUID bug). Additive + read-only: the raw ids stay in
   *  `data`, so `PUT …/details` still round-trips. Unresolvable ids are omitted. */
  dataDisplayRefs: Record<string, CodeDisplayRef>;
  carriedForwardKeys: string[];
}

/** A resolved class-code reference (PMS-S150) — the human code + description for
 *  a canonical Code id stored in the submission field bag. */
export interface CodeDisplayRef {
  id: string;
  code: string;
  description: string;
}

/** GET /api/broker/submissions/{id} — one of the brokerage's own submissions. */
export function getSubmission(
  request: AuthedRequester,
  id: string,
): Promise<SubmissionDetail> {
  return request<SubmissionDetail>(`${BASE}/${encodeURIComponent(id)}`);
}

// ─── Quoting (PMS-S125 PR-2) ──────────────────────────────────────────────────
// The broker triggers a price indication and reads the persisted quotes. The
// broker never names a carrier — PMS fans the indication out across every
// eligible carrier server-side (PMS-S143). Types mirror the PMS wire schemas.

/** An installment option on a quote. */
export interface BrokerPaymentPlan {
  planType: "full" | "monthly" | "quarterly" | "semiannual";
  downPayment: string | null;
  installmentAmount: string | null;
  numberOfInstallments: number | null;
}

/** One RESOLVED offer-term row (PMS-S143): the product manifest's display
 *  schema evaluated against the submission's field bag. `type` reuses the
 *  manifest field types, so the table formats values consistently. */
export interface OfferTermView {
  key: string;
  label: string;
  type: FieldType;
  value: string | number | boolean | string[] | null;
}

/** An ordered group of resolved offer-term rows (the comparison table spine). */
export interface OfferTermsGroupView {
  label: string;
  terms: OfferTermView[];
}

/** A quote as the broker sees it: kind-badged, the financial core + plans, and
 *  the manifest-resolved `offerTerms` (PMS-S143 — replaces the WC-hardcoded
 *  offer-terms superset). Terms describe the insured's requested terms, so
 *  they're the same across a submission's quotes; nullable values render as
 *  "—". */
export interface BrokerQuote {
  id: string;
  kind: "indication" | "quote";
  status: "active" | "expired" | "selected" | "withdrawn";
  carrier: string | null;
  carrierQuoteNumber: string | null;
  totalPremium: string;
  taxesAndFees: string;
  totalCost: string;
  elEachAccident: string | null;
  elEachEmployee: string | null;
  elEachPolicy: string | null;
  policyEffectiveDate: string | null;
  policyExpirationDate: string | null;
  quoteValidUntil: string | null;
  bindStatus: "quotable" | "refer" | "decline" | null;
  isSelected: boolean;
  createdAt: string;
  paymentPlans: BrokerPaymentPlan[];
  offerTerms: OfferTermsGroupView[];
}

/** One carrier's leg of the fan-out indication (PMS-S143): its outcome, its
 *  quote when quoted, and its refer/decline/error reasons. `carrierId`
 *  addresses this carrier at the firm-quote step (BRK-S054). */
export interface CarrierIndication {
  carrierId: string;
  carrier: string;
  outcome: "quoted" | "referred" | "declined" | "error";
  quote: BrokerQuote | null;
  reasons: string[];
}

/** The outcome of a bindable-quote request (single-carrier until PMS-S145). */
export interface IndicationResult {
  outcome: "quoted" | "referred" | "declined";
  quote: BrokerQuote | null;
  referReasons: string[];
  declineReasons: string[];
}

function isMissingField(value: unknown): value is MissingField {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.key === "string" &&
    typeof v.label === "string" &&
    (v.scope === "account" || v.scope === "submission")
  );
}

/** Extract the structured missing-fields list from a quote-readiness 400
 *  (`details.missingFields`, PMS-S144), or null for any other failure — so the
 *  UI renders a field-level checklist instead of a generic error (NN #7). */
export function missingFieldsOf(err: unknown): MissingField[] | null {
  if (!(err instanceof ApiError) || err.status !== 400) return null;
  if (typeof err.body !== "object" || err.body === null) return null;
  const details = (err.body as Record<string, unknown>).details;
  if (typeof details !== "object" || details === null) return null;
  const list = (details as Record<string, unknown>).missingFields;
  if (!Array.isArray(list)) return null;
  const fields = list.filter(isMissingField);
  return fields.length > 0 ? fields : null;
}

/** POST /api/broker/submissions/{id}/price-indication — trigger an indication. */
export function requestPriceIndication(
  request: AuthedRequester,
  id: string,
): Promise<{ results: CarrierIndication[] }> {
  return request<{ results: CarrierIndication[] }>(
    `${BASE}/${encodeURIComponent(id)}/price-indication`,
    { method: "POST" },
  );
}

/** GET /api/broker/submissions/{id}/quotes — the submission's persisted quotes. */
export function listSubmissionQuotes(
  request: AuthedRequester,
  id: string,
): Promise<{ items: BrokerQuote[] }> {
  return request<{ items: BrokerQuote[] }>(
    `${BASE}/${encodeURIComponent(id)}/quotes`,
  );
}

// ─── Carrier-addressed firm quote (PMS-S145, BRK-S054) ────────────────────────

/** One of the addressed carrier's pre-quote questions, in the NORMALIZED shape
 *  every adapter maps into (PIE eligibility questions, Blitz program schema).
 *  Questions the submission's field bag already answers are filtered out
 *  server-side — the SPA never re-asks what PMS knows. */
export interface CarrierQuestion {
  id: string;
  type: "boolean" | "select" | "text" | "number";
  prompt: string;
  options?: string[];
  group?: string;
  required: boolean;
}

/** A broker's answer to one carrier question. */
export interface QuoteAnswer {
  questionId: string;
  answer: string;
  questionText?: string;
}

/** GET /api/broker/submissions/{id}/carriers/{carrierId}/questions — the
 *  addressed carrier's pre-quote questions. The carrierId comes from the
 *  fan-out results (CarrierIndication). */
export function getCarrierQuestions(
  request: AuthedRequester,
  id: string,
  carrierId: string,
): Promise<{ questions: CarrierQuestion[] }> {
  return request<{ questions: CarrierQuestion[] }>(
    `${BASE}/${encodeURIComponent(id)}/carriers/${encodeURIComponent(carrierId)}/questions`,
  );
}

/** POST /api/broker/submissions/{id}/carriers/{carrierId}/quote — firm up the
 *  addressed carrier's indication. The firm quote supersedes that carrier's
 *  indication in the quotes listing, so its column flips in place. */
export function requestCarrierQuote(
  request: AuthedRequester,
  id: string,
  carrierId: string,
  answers: QuoteAnswer[],
): Promise<IndicationResult> {
  return request<IndicationResult>(
    `${BASE}/${encodeURIComponent(id)}/carriers/${encodeURIComponent(carrierId)}/quote`,
    { method: "POST", body: { answers } },
  );
}

// ─── Rating details (PMS-S142, BRK-S052; class codes from PMS-S137) ───────────

/** A class-code option for the class-code typeahead. */
export interface CodeOption {
  id: string;
  code: string;
  typeName: string;
  friendlyName: string | null;
  description: string | null;
}

/** Why a scoped code search came back empty (PMS-S156). `null` for a plain
 *  search miss within a covered state; set only for a SCOPING emptiness:
 *  - `no_state` — no insured state could be resolved (no address / intake state)
 *  - `no_codes_for_state` — no attached State Code Group covers the state. */
export type CodeSearchReason = "no_state" | "no_codes_for_state" | null;

/** GET /api/broker/submissions/{id}/codes — the class-code typeahead SCOPED to a
 *  submission (PMS-S156, replaced the unscoped /api/broker/codes). The server
 *  resolves the product + the insured's state and returns only the codes the
 *  product is in appetite for in that state; `state` echoes the resolved state
 *  and `reason` explains a scoping-empty page so the SPA can say why. */
export interface SubmissionCodeSearch extends Page<CodeOption> {
  state: string | null;
  reason: CodeSearchReason;
}

/** GET /api/broker/submissions/{id}/codes?search= — submission-scoped class-code
 *  search (PMS-S156). Scoping is server-side off the submission id. */
export function searchSubmissionCodes(
  request: AuthedRequester,
  submissionId: string,
  search: string,
  limit = 20,
): Promise<SubmissionCodeSearch> {
  const qs = new URLSearchParams();
  if (search) qs.set("search", search);
  qs.set("limit", String(limit));
  return request<SubmissionCodeSearch>(
    `${BASE}/${encodeURIComponent(submissionId)}/codes?${qs.toString()}`,
  );
}

/** PUT /api/broker/submissions/{id}/details — merge a PARTIAL manifest field
 *  bag into the submission's stored details (PMS-S142; replaces the WC-only
 *  /wc-rating). Keys/value shapes come from the product manifest; effectiveDate
 *  rides in the same body and lands on the typed spine column. Returns the
 *  refreshed detail (rated once it has an effective date). */
export function setSubmissionDetails(
  request: AuthedRequester,
  id: string,
  details: Record<string, unknown>,
): Promise<SubmissionDetail> {
  return request<SubmissionDetail>(
    `${BASE}/${encodeURIComponent(id)}/details`,
    { method: "PUT", body: details },
  );
}
